import React, { FC, useState } from 'react';
import classnames from 'classnames';
import Svg from 'svg-inline-react';
import FrPrivateSvg from '@src/svgs/fr-private.svg';
import FrPublicSvg from '@src/svgs/fr-public.svg';
import FrLockSvg from '@src/svgs/fr-lock.svg';
import FrCheckSvg from '@src/svgs/fr-check.svg';
import FrCloseSvg from '@src/svgs/fr-close.svg';
import { TextField } from '@src/components/TextField';
import {
  doesContainLowercase,
  doesContainNumber,
  doesContainSpecialCharacter,
  doesContainUppercase,
  isPasswordLengthValid,
} from '@src/util/validation';
import { PasswordInputProps } from './props';
import './styles.scss';

export const PasswordInput: FC<PasswordInputProps> = ({
  textFieldProps,
  value,
  isValidationRulesVisible = true,
  placeholder = 'New password',
  errors,
  onChange,
}) => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isValidationRulesActive, setIsValidationRulesActive] = useState(false);

  return (
    <div className="password-input">
      <div className="password-input__pass-field-wrapper">
        <TextField
          defaultValue={value}
          fullWidth={true}
          messageType={errors && 'error'}
          onChange={value => onChange(value)}
          onFocus={() => setIsValidationRulesActive(true)}
          placeholder={placeholder}
          type={isPasswordVisible ? 'text' : 'password'}
          wrapperClassName="password-input__field"
          {...(textFieldProps || {})}
        />

        <Svg
          className="password-input__private-icon"
          onClick={() => setIsPasswordVisible(!isPasswordVisible)}
          src={!isPasswordVisible ? FrPublicSvg : FrPrivateSvg}
        />
      </div>

      {isValidationRulesVisible && isValidationRulesActive ? (
        <div className="password-input__validation-rules-wrapper fade-in">
          <div className="password-input__validation-rules-header">
            <Svg src={FrLockSvg} />
            <span>
              <strong>Your password needs to contain</strong> at least:
            </span>
          </div>

          <div className="password-input__validation-rules">
            <div
              className={classnames(
                'password-input__validation-rule',
                isPasswordLengthValid(value) && 'password-input__validation-rule--active',
                errors?.hasNotEnoughLength && 'password-input__validation-rule--error',
              )}
            >
              {isPasswordLengthValid(value) ? (
                <Svg className="password-input__validation-rule-check" src={FrCheckSvg} />
              ) : null}

              {errors?.hasNotEnoughLength ? (
                <Svg className="password-input__validation-rule-close" src={FrCloseSvg} />
              ) : null}

              <span>10 characters</span>
            </div>

            <div
              className={classnames(
                'password-input__validation-rule',
                doesContainUppercase(value) && 'password-input__validation-rule--active',
                errors?.hasNotUppercase && 'password-input__validation-rule--error',
              )}
            >
              {doesContainUppercase(value) ? (
                <Svg className="password-input__validation-rule-check" src={FrCheckSvg} />
              ) : null}

              {errors?.hasNotUppercase ? (
                <Svg className="password-input__validation-rule-close" src={FrCloseSvg} />
              ) : null}

              <span>one uppercase character</span>
            </div>

            <div
              className={classnames(
                'password-input__validation-rule',
                doesContainLowercase(value) && 'password-input__validation-rule--active',
                errors?.hasNotLowercase && 'password-input__validation-rule--error',
              )}
            >
              {doesContainLowercase(value) ? (
                <Svg className="password-input__validation-rule-check" src={FrCheckSvg} />
              ) : null}

              {errors?.hasNotLowercase ? (
                <Svg className="password-input__validation-rule-close" src={FrCloseSvg} />
              ) : null}

              <span>one lowercase character</span>
            </div>

            <div
              className={classnames(
                'password-input__validation-rule',
                doesContainNumber(value) && 'password-input__validation-rule--active',
                errors?.hasNotNumber && 'password-input__validation-rule--error',
              )}
            >
              {doesContainNumber(value) ? (
                <Svg className="password-input__validation-rule-check" src={FrCheckSvg} />
              ) : null}

              {errors?.hasNotNumber ? <Svg className="password-input__validation-rule-close" src={FrCloseSvg} /> : null}

              <span>one number</span>
            </div>

            <div
              className={classnames(
                'password-input__validation-rule',
                doesContainSpecialCharacter(value) && 'password-input__validation-rule--active',
                errors?.hasNotSpecialCharacter && 'password-input__validation-rule--error',
              )}
            >
              {doesContainSpecialCharacter(value) ? (
                <Svg className="password-input__validation-rule-check" src={FrCheckSvg} />
              ) : null}

              {errors?.hasNotSpecialCharacter ? (
                <Svg className="password-input__validation-rule-close" src={FrCloseSvg} />
              ) : null}

              <span>
                one special character{' '}
                <>
                  !"#$%&'()*+,-./:;{'<'}={'>'}?@[\]^_`{'{'}|{'}'}~
                </>
              </span>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};
